import "./index.css";
import "./i18n-config";

import { config } from "@noted/configuration";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { ToastProvider } from "~/components/alerts/toast-provider";
import AuthProvider from "~/components/auth-provider";
import { intercomScriptInjector } from "~/components/intercom";
import { NavigationState } from "~/components/layout/navigation.context";
import { NetworkLayer } from "~/components/network-layer";
import ThemeProvider from "~/components/theme/theme-provider";

import App from "./app/app";
import safeLocalStorage from "./utilities/safe-local-storage";

if (config.sentryEnvironment) {
  Sentry.init({
    dsn: "https://87e353e6099a423db7dc896e75a4f1b6@sentry.io/1728956",
    environment: config.sentryEnvironment,
    beforeBreadcrumb(breadcrumb) {
      return breadcrumb.category === "console" ? null : breadcrumb;
    },
    release: `krios-${import.meta.env.VITE_COMMIT_SHA}`,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.1,
  });
}

if (config.intercomId) {
  intercomScriptInjector(config.intercomId);
}

Object.defineProperty(window, "localStorage", {
  value: safeLocalStorage,
  configurable: true,
  enumerable: true,
  writable: true,
});

const params = new URLSearchParams(window.location.search);
const session = params.get("s");
if (session) {
  // we have a session in the URL ...
  // extract the session value, store it and redirect to the root with no session value
  window.localStorage.setItem("ngStorage-session", `"${session}"`);
  window.location.href = "/";
} else {
  // no session in the URL, just render the app
  createRoot(document.getElementById("root") as HTMLElement).render(
    <ThemeProvider>
      <ToastProvider>
        <NavigationState>
          <BrowserRouter>
            <NetworkLayer>
              <AuthProvider>
                <App />
              </AuthProvider>
            </NetworkLayer>
          </BrowserRouter>
        </NavigationState>
      </ToastProvider>
    </ThemeProvider>
  );
}
